<template>
  <div class="card border-0 tagihan-info-card">
    <div class="card-body p-0">
      <div class="tagihan-info-grid">
        <!-- ================================================= -->
        <!-- ROW 1 -->
        <!-- ================================================= -->

        <!-- PROYEK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-file-alt"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Proyek
            </div>

            <div class="info-value">
              {{ detail.kode_proyek || '-' }}
            </div>

            <div
              v-if="detail.nama_proyek"
              class="info-subvalue"
              :title="detail.nama_proyek"
            >
              {{ detail.nama_proyek }}
            </div>
          </div>
        </div>

        <!-- TANGGAL KONTRAK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-calendar-alt"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Tanggal Kontrak
            </div>

            <div class="info-value">
              {{ dateFormat(detail.tanggal_mulai) }}
              -
              {{ dateFormat(detail.tanggal_berakhir) }}
            </div>
          </div>
        </div>

        <!-- MEKANISME PEMBAYARAN -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-wallet"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Mekanisme Pembayaran
            </div>

            <div class="info-value">
              {{ detail.mekanisme_pembayaran || '-' }}
            </div>
          </div>
        </div>

        <!-- JUMLAH TAHAPAN -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-list-ul"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Jumlah Tahapan
            </div>

            <div class="info-value">
              {{ detail.jumlah_tahapan_pembayaran || '0' }}
            </div>
          </div>
        </div>

        <!-- NILAI KONTRAK -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-money-bill"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Nilai Kontrak
            </div>

            <div class="info-value">
              {{ formatRupiah(detail.nilai_kontrak_belum_ppn) }}
            </div>
          </div>
        </div>

        <!-- ================================================= -->
        <!-- ROW 2 -->
        <!-- ================================================= -->

        <!-- TOTAL TAGIHAN -->
        <div class="info-item">
          <div class="info-icon icon-primary">
            <i class="uil uil-calculator"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Total Tagihan
            </div>

            <div class="info-value">
              {{ formatRupiah(totalTagihan) }}
            </div>

            <div class="info-subvalue">
              Prorate:
              {{ formatRupiah(detail.nilai_kontrak_prorate) }}
            </div>
          </div>
        </div>

        <!-- SUDAH DIBAYAR -->
        <div class="info-item">
          <div class="info-icon icon-success">
            <i class="uil uil-check-circle"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Sudah Dibayar
            </div>

            <div class="info-value text-success">
              {{ formatRupiah(totalSudahDibayar) }}
            </div>
          </div>
        </div>

        <!-- BELUM DIBAYAR -->
        <div class="info-item">
          <div class="info-icon icon-warning">
            <i class="uil uil-clock"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Belum Dibayar
            </div>

            <div
              class="info-value"
              :class="totalBelumDibayar > 0 ? 'text-danger' : 'text-success'"
            >
              {{ formatRupiah(totalBelumDibayar) }}
            </div>
          </div>
        </div>

        <!-- TOTAL BEBAN -->
        <div class="info-item">
          <div class="info-icon icon-warning">
            <i class="uil uil-money-withdraw"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Total Beban
            </div>

            <div class="info-value">
              {{ formatRupiah(totalBeban) }}
            </div>
          </div>
        </div>

        <!-- MARGIN -->
        <div class="info-item">
          <div
            class="info-icon"
            :class="Number(margin) < 0 ? 'icon-danger' : 'icon-success'"
          >
            <i class="uil uil-chart-growth"></i>
          </div>

          <div class="info-content">
            <div class="info-label">
              Margin
            </div>

            <div
              class="info-value"
              :class="Number(margin) < 0 ? 'text-danger' : 'text-success'"
            >
              {{ (Number(margin) * 100).toFixed(2) }}%
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'TagihanInfo',

  props: {
    detail: {
      type: Object,
      required: true,
    },

    items: {
      type: Array,
      default: () => [],
    },

    margin: {
      type: [Number, String],
      default: 0,
    },
  },

  computed: {
    /**
     * Total tagihan dari seluruh item.
     */
    totalTagihan() {
      return this.items.reduce(
        (total, item) => total + Number(item.total_tagihan || 0),
        0
      )
    },

    /**
     * Total tagihan yang sudah dibayar.
     */
    totalSudahDibayar() {
      return this.items.reduce((total, item) => {
        if (item.tanggal_bayar !== null) {
          return total + Number(item.total_tagihan || 0)
        }

        return total
      }, 0)
    },

    /**
     * Total tagihan yang belum dibayar.
     */
    totalBelumDibayar() {
      return this.items.reduce((total, item) => {
        if (item.tanggal_bayar === null) {
          return total + Number(item.total_tagihan || 0)
        }

        return total
      }, 0)
    },

    /**
     * Total beban vendor.
     */
    totalBeban() {
      return this.items.reduce(
        (total, item) => total + Number(item.beban_sph_vendor || 0),
        0
      )
    },
  },

  methods: {
    formatRupiah(value) {
      return `Rp ${Number(value || 0).toLocaleString('id-ID')}`
    },

    dateFormat(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '-'
    },
  },
}
</script>

<style scoped>
.tagihan-info-card {
  border: 1px solid #cfe2ff !important;
  border-radius: 8px;
  background: #f8fbff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.tagihan-info-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.info-item {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 92px;
  padding: 12px 15px;
  border-right: 1px solid #dbe7f5;
  border-bottom: 1px solid #dbe7f5;
}

.info-item:nth-child(5n) {
  border-right: 0;
}

.info-item:nth-last-child(-n + 5) {
  border-bottom: 0;
}

/* =========================================
   ICON
   ========================================= */

.info-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: 12px;
  border-radius: 50%;

  font-size: 20px;
}

.icon-primary {
  color: #3867f0;
  background: #e8f1ff;
}

.icon-success {
  color: #20bf82;
  background: #e2f7ed;
}

.icon-warning {
  color: #3867f0;
  background: #e8f1ff;
}

.icon-danger {
  color: #f46a6a;
  background: #fdeaea;
}

/* =========================================
   CONTENT
   ========================================= */

.info-content {
  min-width: 0;
  flex: 1;
}

.info-label {
  margin-bottom: 3px;
  color: #53627a;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.info-value {
  color: #15213b;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-word;
}

.info-subvalue {
  margin-top: 2px;
  color: #7c8799;
  font-size: 11px;
  line-height: 1.3;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =========================================
   RESPONSIVE
   ========================================= */

@media (max-width: 1199.98px) {
  .tagihan-info-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .info-item {
    min-height: 92px;
    padding: 12px 14px;
  }

  .info-item:nth-child(5n) {
    border-right: 1px solid #dbe7f5;
  }

  .info-item:nth-child(3n) {
    border-right: 0;
  }

  .info-item:nth-last-child(-n + 5) {
    border-bottom: 1px solid #dbe7f5;
  }

  .info-item:nth-last-child(-n + 3) {
    border-bottom: 0;
  }
}

/* =========================================
   MOBILE
   ========================================= */

@media (max-width: 767.98px) {
  .tagihan-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .info-item {
    min-height: 82px;
    padding: 11px 12px;
  }

  .info-item:nth-child(3n) {
    border-right: 1px solid #dbe7f5;
  }

  .info-item:nth-child(2n) {
    border-right: 0;
  }

  .info-item:nth-last-child(-n + 3) {
    border-bottom: 1px solid #dbe7f5;
  }

  .info-item:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  /* ICON */

  .info-icon {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;

    margin-right: 9px;
    font-size: 17px;
  }

  /* TEXT */

  .info-label {
    margin-bottom: 2px;
    font-size: 11px;
  }

  .info-value {
    font-size: 14px;
  }

  .info-subvalue {
    font-size: 10px;
  }
}

/* =========================================
   EXTRA SMALL
   ========================================= */

@media (max-width: 575.98px) {
  .tagihan-info-grid {
    grid-template-columns: 1fr;
  }

  .info-item,
  .info-item:nth-child(2n),
  .info-item:nth-child(3n) {
    border-right: 0 !important;
    border-bottom: 1px solid #dbe7f5 !important;
  }

  .info-item:last-child {
    border-bottom: 0 !important;
  }

  .info-item {
    min-height: 72px;
    padding: 10px 12px;
  }

  .info-icon {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;

    margin-right: 10px;
    font-size: 17px;
  }

  .info-label {
    font-size: 11px;
  }

  .info-value {
    font-size: 14px;
  }

  .info-subvalue {
    font-size: 10px;
  }
}
</style>
